import { Button } from '@/modules/Button';
import type { ButtonSlotConfig } from '@/shared/types/buttonConfig';
import type { SwitchOption, SwitchProps } from '@/shared/types/switch';
import type { IconFontColor, IconFontSize } from '@/shared/types/types';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { useState, useEffect } from 'react';

/** Tipografía del label en control `md`: tamaño/interlineado/tracking Regular + peso Bold (spec Type/Body/SM). */
const SWITCH_MD_LABEL_TYPOGRAPHY =
  'ui-text-body-sm-regular ui-font-body-sm-bold ui-leading-body-sm-regular ui-tracking-body-sm';

function optionSlotToButtonSlotConfig(
  icon: SwitchOption['leftSlot'],
  colorIcon: IconFontColor | undefined,
  duotone: Pick<
    ButtonSlotConfig,
    | 'iconDuotonePrimary'
    | 'iconDuotoneSecondary'
    | 'iconDuotoneOpacityPrimary'
    | 'iconDuotoneOpacitySecondary'
  >,
  defaultIconFontSize: IconFontSize,
  optionIconFontSize?: IconFontSize
): ButtonSlotConfig | undefined {
  if (icon === null || icon === undefined) {
    return undefined;
  }
  return {
    icon,
    ...(colorIcon !== undefined ? { colorIcon } : {}),
    ...duotone,
    iconFontSize: optionIconFontSize ?? defaultIconFontSize,
  };
}

/**
 * Switch - Componente de switch/toggle con múltiples opciones
 *
 * Componente que permite seleccionar entre múltiples opciones mediante botones toggle.
 * Soporta estados controlados y no controlados, diferentes tamaños y ancho completo.
 *
 * @example
 * ```tsx
 * // Switch simple
 * <Switch
 *   options={[
 *     { label: 'Opción 1', value: 'opt1' },
 *     { label: 'Opción 2', value: 'opt2' }
 *   ]}
 *   defaultValue="opt1"
 *   onChange={(value) => console.log(value)}
 * />
 *
 * // Switch de ancho completo
 * <Switch
 *   options={[
 *     { label: 'Vista', value: 'view' },
 *     { label: 'Editar', value: 'edit' }
 *   ]}
 *   fullWidth
 *   size="md"
 * />
 *
 * // Iconos de slot más grandes (o por opción con `leftSlotIconSize`)
 * <Switch options={[{ label: 'A', value: 'a', leftSlot: myIcon }]} slotIconSize="md" />
 * ```
 *
 * @see {@link SwitchProps} para la lista completa de props
 */
export const Switch = ({
  options,
  defaultValue,
  onChange,
  size,
  colorIcon = 'primary',
  fullWidth = false,
  slotIconSize,
}: SwitchProps) => {
  /** `sm` siempre que no se pase tamaño o venga `undefined` (p. ej. tras spread de props). */
  const controlSize = size ?? 'sm';

  const variants = cva('', {
    variants: {
      size: {
        /** Padding del track 6px (`--ui-Positive-space_6`). */
        xs: 'ui-p-6 ui-gap-2',
        sm: 'ui-p-6 ui-gap-2',
        /** Padding del track 8px (`--ui-Positive-space_8`). */
        md: 'ui-p-8 ui-gap-2',
      },
      fullWidth: {
        true: 'ui-w-full',
        false: 'ui-min-w-max',
      },
    },
    defaultVariants: {
      size: 'sm',
      fullWidth: false,
    },
  });

  /**
   * `size` xs | sm → botones `xxs`. `size` md → botones `xs` (padding track 8px + tipografía SM híbrida).
   */
  const sizeButton = controlSize === 'xs' ? 'xxs' : controlSize === 'sm' ? 'xxs' : 'xs';

  /**
   * Sin `slotIconSize`: con control `sm`, iconos por defecto `xs`; con `xs`, `xs`; con `md`, `sm` (resto como antes).
   */
  const defaultSlotIconSize: IconFontSize =
    slotIconSize ?? (controlSize === 'xs' ? 'xs' : controlSize === 'sm' ? 'xs' : 'sm');

  const [selectedValue, setSelectedValue] = useState(defaultValue || options[0]?.value || '');

  // Actualizar el valor seleccionado cuando cambia defaultValue
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== selectedValue) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue, selectedValue]);

  const handleClick = (value: string) => {
    const option = options.find((o) => o.value === value);
    if (option?.disabled) {
      return;
    }

    if (selectedValue === value) {
      return;
    }

    setSelectedValue(value);
    if (onChange) {
      onChange(value);
    }
  };

  const classes = variants({ size: controlSize, fullWidth });

  return (
    <div
      className={cn(
        'ui-imj',
        'ui-relative ui-flex ui-gap-8 ui-rounded-control-lg ui-bg-fill-tertiary',
        classes
      )}
    >
      {/* Botones de opciones */}
      {options.map((option) => (
        <Button
          size={sizeButton}
          key={option.value}
          className={controlSize === 'md' ? SWITCH_MD_LABEL_TYPOGRAPHY : undefined}
          onClick={() => handleClick(option.value)}
          fullWidth={fullWidth}
          disabled={option.disabled}
          clickable={!option.disabled && selectedValue !== option.value}
          color={selectedValue === option.value ? 'soft' : 'tertiary'}
          active={selectedValue === option.value}
          leftSlotProps={optionSlotToButtonSlotConfig(
            option.leftSlot,
            colorIcon,
            {
              iconDuotonePrimary: option.leftSlotDuotonePrimary,
              iconDuotoneSecondary: option.leftSlotDuotoneSecondary,
              iconDuotoneOpacityPrimary: option.leftSlotDuotoneOpacityPrimary,
              iconDuotoneOpacitySecondary: option.leftSlotDuotoneOpacitySecondary,
            },
            defaultSlotIconSize,
            option.leftSlotIconSize
          )}
          rightSlotProps={optionSlotToButtonSlotConfig(
            option.rightSlot,
            colorIcon,
            {
              iconDuotonePrimary: option.rightSlotDuotonePrimary,
              iconDuotoneSecondary: option.rightSlotDuotoneSecondary,
              iconDuotoneOpacityPrimary: option.rightSlotDuotoneOpacityPrimary,
              iconDuotoneOpacitySecondary: option.rightSlotDuotoneOpacitySecondary,
            },
            defaultSlotIconSize,
            option.rightSlotIconSize
          )}
          pill="xs"
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
};
